import { Component, OnInit, Renderer2, RendererFactory2 } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { AccountPanelEvent } from 'app/core/auth/auth.events';
import { selectCurrentUser, userHasAnyACL } from 'app/shared/state/user.selectors';
import * as moment from 'moment'; // NOSONAR: This import is necessary for moment usage. Default import doesn't work.
import { EMPTY, Observable, Subject } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { AuthServerProvider } from '../../core/auth/auth-jwt.service';
import { StateStorageService } from '../../core/auth/state-storage.service';
import { EndSessionModalService } from '../../core/endSession/end-session-modal.service';
import { AppConfiguration } from '../../core/models/app-configuration.model';
import { AccountACL } from '../../shared/constants/account-acl.constants';
import { UserInfo } from '../../shared/model/user-info.model';
import { UserInfoService } from '../../shared/service/user-info.service';
import { AppState } from '../../shared/state/app.state';
import { PasswordExpireAdvisorModalComponent } from './password-expire-adv.modal.component';
import { TwoFactorClientExpireAdvisorModalComponent } from './twofactor-client-expire-adv.modal.component';
import { selectCurrent2FaClientStatus } from '../../shared/state/twofa-client-infos.selectors';
import { PecPlusService } from 'app/shared/service/pec-plus.service';
import { PecConversionExpireAdvisorModalComponent } from './pec-conversion-expire-adv.modal.component';
import { PecConversion3ExpireAdvisorModalComponent } from './pec-conversion3-expire-adv.modal.component';
import { HttpResponse } from '@angular/common/http';
import { WebMail } from 'app/shared/model/web-mail.model';
import { WebMailService } from 'app/shared/service/web_mail.service';
import { Account } from '../../core/models/account.model';
import { retrieveduserInfoPecPlus } from 'app/shared/state/user.actions';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Timeouts } from 'app/shared/constants/timeout.constants';
import { ConversionStrategy, RecognitionStatus, UpgradableStatus } from 'app/shared/constants/user.constants';

/**
 * Angular component contain primary html structure of the application
 */
@Component({
  selector: 'ap-main',
  templateUrl: './main.component.html',
})
export class MainComponent implements OnInit {
  private readonly renderer: Renderer2;

  private readonly events: Observable<AccountPanelEvent>;

  destroy$: Subject<boolean> = new Subject<boolean>();

  fullContent = false;

  expirationDate!: moment.Moment;

  urlPGA?: string = '';
  urlWebmail?: string = '';
  currentCompany?: string;
  mailbox?: string;
  usertype?: string;
  userAgent?: string;
  acls = AccountACL;
  currentUser: Account | null = null;

  constructor(
    private readonly titleService: Title,
    private readonly router: Router,
    private readonly translateService: TranslateService,
    private readonly rootRenderer: RendererFactory2,
    private readonly authServerProvider: AuthServerProvider,
    private readonly endSessionService: EndSessionModalService,
    private readonly userInfoService: UserInfoService,
    private readonly appConfig: AppConfiguration,
    private readonly modalService: NgbModal,
    private readonly store: Store<AppState>,
    private readonly stateStorageService: StateStorageService,
    private readonly pecPlusService: PecPlusService,
    private readonly webMailService: WebMailService,
    private readonly deviceService: DeviceDetectorService
  ) {
    this.renderer = this.rootRenderer.createRenderer(document.querySelector('html'), null);
    this.events = this.authServerProvider.eventsSubject.asObservable();
    this.userAgent = navigator.userAgent;
  }

  /**
   * Component initialization
   * Register for some application event, like language change, session expiration and login completition
   * Set mailbox, userType and currentuser values based on the store currentUser value.
   */
  ngOnInit(): void {
    this.getExpirationDate();
    this.getUrls();
    this.getCurrentUser();
    this.subscribeToRouterEvents();
    this.subscribeToLanguageChange();
    this.subscribeToEvents();
  }

  private getExpirationDate() {
    this.expirationDate = this.appConfig.pecConversionExpireDate;
  }

  private getUrls() {
    this.urlPGA = this.appConfig.pgaUrl + 'security/2stepverification';
    this.urlWebmail = this.appConfig.webmailUrl;
    this.currentCompany = this.stateStorageService.getLoggedCompany();
    if (this.currentCompany !== 'pec') {
      const redirectPga = this.currentCompany || '';
      this.webMailService.query(redirectPga).subscribe((res: HttpResponse<WebMail>) => {
        if (res.body?.redirectWebMail) {
          this.urlWebmail = res.body.redirectWebMail;
          this.urlPGA = res.body.redirectPGA || '';
        }
      });
    }
  }

  private getCurrentUser() {
    this.store
      .select(selectCurrentUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((account: Account | null) => {
        if (account) {
          this.mailbox = account.lastname;
          this.usertype = account.userType;
          this.currentUser = account;
        }
      });
  }

  private subscribeToRouterEvents() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.updateTitleAndLayout();
      }
    });
  }

  private subscribeToLanguageChange() {
    this.translateService.onLangChange.subscribe((langChangeEvent: LangChangeEvent) => {
      this.updateTitleAndLayout();

      this.renderer.setAttribute(document.querySelector('html'), 'lang', langChangeEvent.lang);
    });
  }

  private subscribeToEvents() {
    this.events
      .pipe(filter((e: AccountPanelEvent) => e.type === 'session_expires' || e.type === 'login_completed'))
      .subscribe((e: AccountPanelEvent) => {
        if (e.type === 'login_completed') {
          this.handleLoginCompletedEvent(e);
        }
        if (e.type === 'session_expires') {
          this.handleSessionExpiresEvent();
        }
      });
  }

  private handleSessionExpiresEvent() {
    // forced timeout
    setTimeout(() => {
      this.endSessionService.open();
    }, Timeouts.TIMEOUT_100);
  }

  private handleLoginCompletedEvent(e: AccountPanelEvent) {
    const account = e.info?.account;
    if (!account) {
      return;
    }
    // case multiutente con 2fa di tipo email attiva
    if (account.acls.some((authority: AccountACL) => [AccountACL.TWOFA_EMAIL_VIEW].includes(authority))) {
      this.router.navigate(['/', this.currentCompany, 'security', '2stepverification']);
    } else {
      if (!account.acls.some((authority: AccountACL) => [AccountACL.TWOFA_APP_WITH_CHILDREN_VIEW].includes(authority))) {
        this.afterLoginCheckAdeguamentoPec();
      }
      if (account.acls.some((authority: AccountACL) => [AccountACL.CLIENT_API].includes(authority))) {
        this.afterLoginCheck2faClient();
      }
    }

    if (account.acls.some((authority: AccountACL) => [AccountACL.PASSWORD_INFO_VIEW].includes(authority))) {
      this.afterLoginCheckUserInfo();
    }
  }

  /**
   * Subscribes to current 2FA client status, checks whether user has valid API key,
   * calculates expiration days left and displays model 2FA client advisor on desktop devices
   * if expiration days is less than or equal to 14 and renew counter is more or equal to its maximum value
   */
  private afterLoginCheck2faClient(): void {
    const count = 2;
    this.store
      .select(selectCurrent2FaClientStatus)
      .pipe(take(count)) // modificato per visualizzare modale scadenza m.x
      .subscribe(twofaClientStastus => {
        if (twofaClientStastus?.hasValidApiKey) {
          const expireted = moment(twofaClientStastus.expireTsLastApiKey).format('DD/MM/YYYY');
          const expired = moment(twofaClientStastus.expireTsLastApiKey).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
          const currenDate = moment(new Date()).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
          const expirationDays = expired.diff(currenDate, 'days');
          const renewCounterApiKey = twofaClientStastus.renewCounterApiKey || 0;
          const maxRenewCounterApiKey = twofaClientStastus.maxRenewCounterApiKey || 0;
          const twoWeeks = 14;
          const isExpiringOrRenewable = maxRenewCounterApiKey > 0 || expirationDays <= twoWeeks;
          const isExpiringOrRenewalLimitReached = expirationDays <= twoWeeks || renewCounterApiKey >= maxRenewCounterApiKey;
          const isNotMobile = !this.deviceService.isMobile(this.userAgent);
          if (isExpiringOrRenewable && isExpiringOrRenewalLimitReached && isNotMobile) {
            this.showModal2faClientAdvisor(expirationDays, expireted, renewCounterApiKey, maxRenewCounterApiKey);
          }
        }
      });
  }

  /**
   * Opens TwoFactorClientExpireAdvisorModalComponent.
   * Sets modal label to 'renew' if renew counter exceeds its maximum value.
   * Sets modal label to 'expired' if expiration date has passed or 'nearly' if not.
   * Navigates to 2step verification after modal is closed.
   * @param {number} expirationDays Days before password expires
   * @param {string} expirationDate Date before password expires
   * @param {number} renewCounterApiKey The amount of times api key has been renewed
   * @param {number} maxRenewCounterApiKey The maximum amount of times api key can be renewed
   */
  private showModal2faClientAdvisor(
    expirationDays: number,
    expirationDate: string,
    renewCounterApiKey: number,
    maxRenewCounterApiKey: number
  ): void {
    const modalRef: NgbModalRef = this.modalService.open(TwoFactorClientExpireAdvisorModalComponent, { size: 'lg' });
    if (renewCounterApiKey >= maxRenewCounterApiKey) {
      modalRef.componentInstance.label = 'renew';
    } else {
      modalRef.componentInstance.label = expirationDays <= 0 ? 'expired' : 'nearly';
    }
    modalRef.componentInstance.expirationDate = expirationDate;
    modalRef.result.then(
      () => {
        this.router.navigate([this.stateStorageService.getLoggedCompany(), 'security', '2stepverification'], { fragment: '2fa-client' });
      },
      () => { }
    );
  }

  /**
   * Retrieve user Info and do some check:
   * - Send user to global password change site if password expired or is nearly expiration
   * - Show password expiration info modal if needed
   */
  private afterLoginCheckUserInfo(): void {
    this.userInfoService.getUserInfo().subscribe((userInfo: UserInfo | null) => {
      if (!userInfo) {
        return;
      }
      const {
        passwordExpirationTime,
        alertPasswordChangeTime,
        showAlertChangePassword,
        alertChangePassword,
        changePassword,
        expiredPassword,
      } = userInfo;
      const isPasswordExpired = passwordExpirationTime?.isSameOrBefore(moment().endOf('day'));
      if (changePassword || expiredPassword || isPasswordExpired) {
        this.goToGlobalPasswordChange();
        return;
      }
      const shouldShowPasswordChangeAlert = showAlertChangePassword && alertChangePassword;
      const isTimeToShowAlert = passwordExpirationTime && moment().isBetween(alertPasswordChangeTime, passwordExpirationTime);
      if (this.router.url.includes('/security/changepassword') && shouldShowPasswordChangeAlert && isTimeToShowAlert) {
        this.showModalPasswordAdvisor(passwordExpirationTime.startOf('day').diff(moment().startOf('day'), 'days'));
      }
    });
  }

  /**
   * Redirect user to Global password change flow
   */
  private goToGlobalPasswordChange(): void {
    let globalPasswordUri;
    if (this.verifyAcls(this.acls.DOCAPOSTE_VIEW)) {
      globalPasswordUri = this.appConfig.globalChangePasswordUri;
    } else {
      globalPasswordUri = this.appConfig.globalChangePasswordUriDocaposte;
    }
    if (globalPasswordUri) {
      const { protocol, host, pathname } = window.location;
      const currentOrigin = `${protocol}//${host}${pathname}`;
      const myform = document.createElement('form');
      myform.method = 'POST';
      myform.action = globalPasswordUri;
      myform.style.display = 'none';
      myform.append('Content-Type', 'application/x-www-form-urlencoded');

      const redirectUrl = document.createElement('input');
      redirectUrl.type = 'hidden';
      redirectUrl.name = 'redirect_url';
      redirectUrl.value = currentOrigin;
      myform.appendChild(redirectUrl);

      const token = document.createElement('input');
      token.type = 'hidden';
      token.name = 'token';
      token.value = this.authServerProvider.getToken();
      myform.appendChild(token);

      document.body.appendChild(myform);
      myform.submit();
    }
  }

  /**
   * Open Password expiration Info modal
   * @param expireDays Days before password expire
   */
  private showModalPasswordAdvisor(expireDays: number): void {
    const modalRef: NgbModalRef = this.modalService.open(PasswordExpireAdvisorModalComponent, { size: 'lg' });
    modalRef.componentInstance.expirationDays = expireDays;

    modalRef.result.then(retObj => {
      if (!retObj) {
        return;
      }

      if (retObj.hideModal) {
        this.store
          .select(userHasAnyACL, { acls: AccountACL.PASSWORD_INFO_UPDATE })
          .pipe(switchMap((hasAcl: boolean) => (hasAcl ? this.userInfoService.hidePasswordExpireAdvisor() : EMPTY)))
          .subscribe();
      }

      if (retObj.goToChangePassword) {
        this.router.navigate([this.stateStorageService.getLoggedCompany(), 'security', 'changepassword']);
      }
    });
  }

  /**
   * Utility method used from template for retrieving current page title
   * @param routeSnapshot current routing information
   */
  private getPageTitle(routeSnapshot: ActivatedRouteSnapshot): string {
    let title: string = routeSnapshot.data && routeSnapshot.data['pageTitle'] ? routeSnapshot.data['pageTitle'] : '';
    if (routeSnapshot.firstChild) {
      title = this.getPageTitle(routeSnapshot.firstChild) || title;
    }
    return title;
  }

  /**
   * Utility method used from template for retrieving page layout (full or not)
   * @param routeSnapshot current routing information
   */
  private getLayout(routeSnapshot: ActivatedRouteSnapshot): string {
    let layout: string = routeSnapshot.data && routeSnapshot.data['layout'] ? routeSnapshot.data['layout'] : 'std';
    if (routeSnapshot.firstChild) {
      layout = this.getLayout(routeSnapshot.firstChild) || layout;
    }

    return layout;
  }

  /**
   * After language change update Html page title and layout on template
   */
  private updateTitleAndLayout(): void {
    const pageTitle = this.getPageTitle(this.router.routerState.snapshot.root);

    const layout = this.getLayout(this.router.routerState.snapshot.root);
    if (layout === 'full') {
      this.fullContent = true;
    } else {
      this.fullContent = false;
    }

    this.translateService.get([pageTitle, 'main.appTitle']).subscribe((translations: { [key: string]: string }) => {
      const finalTitle = `${translations[pageTitle]} - ${translations['main.appTitle']}`;
      this.titleService.setTitle(finalTitle);
    });
  }

  /**
   * Check Adeguamento PEC based on conversion strategy
   *
   * Mock values to use for local testing:
   *
   * mock strategy 3
    * strategia gialla
    const conversionStrategy = ConversionStrategy.THREE;
    const recognitionStatus = RecognitionStatus.DOCUMENTS_REQUIRED;
    const isRecognitionStatusInvalid = true;
    const territoryExpireDate = "2022-09-02T08:12:33Z";
    const dvoExpireDate = "2023-02-28";
    const twoFAStatus = true;

     * mock strategy 4
     * strategia rossa

    const conversionStrategy = ConversionStrategy.FOUR;
    const isRecognitionStatusInvalid = true;
    const recognitionStatus = RecognitionStatus.DOCUMENTS_REQUIRED;
    const territoryExpireDate = '2022-09-02T08:12:33Z';
    const dvoExpireDate = '2023-02-28';
    const twoFAStatus = true;

     * mock strategy 5
     * strategia gialla

    const conversionStrategy = ConversionStrategy.FIVE;
    const recognitionStatus = RecognitionStatus.DOCUMENTS_REQUIRED;
    const isRecognitionStatusInvalid = true;
    const territoryExpireDate = '2022-09-02T08:12:33Z';
    const twoFAStatus = true;
    const dvoExpireDate = '2023-02-28';
  */

  private afterLoginCheckAdeguamentoPec(): void {
    this.pecPlusService.getUserInfo().subscribe((userInfo: UserInfo | null) => {
      if (!userInfo) {
        return;
      }
      this.store.dispatch(retrieveduserInfoPecPlus({ userInfoPecPlus: userInfo }));
      const { recognitionStatus, conversionStrategy, twoFAStatus, salesChannel, territoryExpireDate, dvoExpireDate } =
        this.getAutoLoginData(userInfo);
      const isRecognitionStatusInvalid = this.getIsRecognitionStatusInvalid(recognitionStatus, twoFAStatus);
      if (this.usertype !== 'pec-multiutente' && userInfo.upgradableStatus !== UpgradableStatus.JUST_UPGRADED) {
        if (
          this.getShouldShowModalForStrategyThree(conversionStrategy, isRecognitionStatusInvalid) ||
          this.getShouldShowModalForStrategyFive(conversionStrategy, recognitionStatus)
        ) {
          this.showModalPecConversion3ExpireAdvisor(recognitionStatus, twoFAStatus, salesChannel, territoryExpireDate, dvoExpireDate);
        }
        if (this.getShouldShowModalForStrategyFour(conversionStrategy, isRecognitionStatusInvalid)) {
          const dataDif = moment(this.expirationDate).startOf('day').diff(moment().startOf('day'), 'days') || 0;
          this.showModalPecConversionExpireAdvisor(
            dataDif > 0 ? dataDif : 0,
            recognitionStatus,
            twoFAStatus,
            salesChannel,
            territoryExpireDate,
            dvoExpireDate
          );
        }
      }
    });
  }

  private getAutoLoginData(userInfo: UserInfo) {
    return {
      salesChannel: userInfo.salesChannel || '',
      recognitionStatus: userInfo.recognitionStatus || '',
      twoFAStatus: userInfo.twoFAStatus || false,
      territoryExpireDate: userInfo.territoryExpireDate || '',
      dvoExpireDate: userInfo.dvoExpireDate || '',
      conversionStrategy: userInfo.conversionStrategy,
    };
  }

  private getIsRecognitionStatusInvalid(recognitionStatus: RecognitionStatus | string, twoFAStatus: boolean) {
    return recognitionStatus !== RecognitionStatus.OK || (recognitionStatus === RecognitionStatus.OK && !twoFAStatus);
  }

  private getShouldShowModalForStrategyThree(conversionStrategy: number | null, isRecognitionStatusInvalid: boolean) {
    return conversionStrategy === ConversionStrategy.THREE && isRecognitionStatusInvalid;
  }

  private getShouldShowModalForStrategyFive(conversionStrategy: number | null, recognitionStatus: RecognitionStatus | string) {
    return (
      conversionStrategy === ConversionStrategy.FIVE &&
      recognitionStatus !== RecognitionStatus.IDENTIFICATION_IN_PROGRESS &&
      recognitionStatus !== RecognitionStatus.OK
    );
  }

  private getShouldShowModalForStrategyFour(conversionStrategy: number | null, isRecognitionStatusInvalid: boolean) {
    return conversionStrategy === ConversionStrategy.FOUR && isRecognitionStatusInvalid;
  }

  /**
   * Adeguamento Pec Info modal
   * @param expireDays Days before pec+ expire
   */
  private showModalPecConversionExpireAdvisor(
    expireDays: number,
    recognitionStatus: string,
    twoFaEnabled: boolean,
    salesChannel: string,
    territoryExpireDate: string,
    dvoExpiryDate: string
  ): void {
    const modalRef: NgbModalRef = this.modalService.open(PecConversionExpireAdvisorModalComponent, { size: 'xl' });
    modalRef.componentInstance.expireDays = expireDays;
    modalRef.componentInstance.urlGuide = this.appConfig.guideQuestionPecPlus;
    modalRef.componentInstance.TIME_LIMIT = this.appConfig.pecConversionRememberTimeout;
    modalRef.componentInstance.recognitionStatus = recognitionStatus;
    modalRef.componentInstance.territoryExpireDate = territoryExpireDate;
    modalRef.componentInstance.dvoExpireDate = dvoExpiryDate;
    modalRef.componentInstance.twoFaEnabled = twoFaEnabled;
    modalRef.componentInstance.urlWebmail = this.urlWebmail;
    modalRef.componentInstance.urlPGA = this.urlPGA;
    modalRef.componentInstance.salesChannel = salesChannel;
    modalRef.componentInstance.mailbox = this.mailbox;
    modalRef.result.then(retObj => {
      if (!retObj) {
        return;
      }
    });
  }
  /**
   * Adeguamento Pec Info modal
   */
  private showModalPecConversion3ExpireAdvisor(
    recognitionStatus: string,
    twoFaEnabled: boolean,
    salesChannel: string,
    territoryExpireDate: string,
    dvoExpireDate: string
  ): void {
    const modalRef: NgbModalRef = this.modalService.open(PecConversion3ExpireAdvisorModalComponent, { size: 'xl' });
    modalRef.componentInstance.urlGuide = this.appConfig.guideQuestionPecPlus;
    modalRef.componentInstance.recognitionStatus = recognitionStatus;
    modalRef.componentInstance.twoFaEnabled = twoFaEnabled;
    modalRef.componentInstance.territoryExpireDate = territoryExpireDate;
    modalRef.componentInstance.dvoExpireDate = dvoExpireDate;
    modalRef.componentInstance.urlWebmail = this.urlWebmail;
    modalRef.componentInstance.urlPGA = this.urlPGA;
    modalRef.componentInstance.salesChannel = salesChannel;
    modalRef.componentInstance.mailbox = this.mailbox;
    modalRef.result.then(retObj => {
      if (!retObj) {
        return;
      }
    });
  }

  /**
   * Check a specific ACL for user
   * @param acl the access control list to check
   */
  verifyAcls(acl: AccountACL): boolean {
    return !this.currentUser?.acls.some((authority: AccountACL) => [acl].includes(authority));
  }
}
